import React, { useState } from 'react'
import { AlertTriangle, CheckCircle, XCircle, Info, Copy, ExternalLink } from 'lucide-react'
import { apiService } from '../../services/api'

interface ConfigurationStatusProps {
  showDetails?: boolean
  className?: string
}

export const ConfigurationStatus: React.FC<ConfigurationStatusProps> = ({ 
  showDetails = true, 
  className = '' 
}) => {
  const [copied, setCopied] = useState(false)
  const config = apiService.getConfiguration()
  const isValid = apiService.isConfigurationValid()
  const error = apiService.getConfigurationError()

  const copyToClipboard = (text: string) => {
    navigator.clipboard.writeText(text).then(() => {
      setCopied(true)
      setTimeout(() => setCopied(false), 2000)
    })
  }

  const getStatusIcon = () => {
    if (isValid) {
      if (config.environment === 'development' && config.baseUrl === 'https://api.example.com') {
        return <AlertTriangle className="h-5 w-5 text-yellow-500" />
      }
      return <CheckCircle className="h-5 w-5 text-green-500" />
    }
    return <XCircle className="h-5 w-5 text-red-500" />
  }

  const getStatusColor = () => {
    if (isValid) {
      if (config.environment === 'development' && config.baseUrl === 'https://api.example.com') {
        return 'border-yellow-200 bg-yellow-50'
      }
      return 'border-green-200 bg-green-50'
    }
    return 'border-red-200 bg-red-50'
  }

  const getTroubleshootingSteps = () => {
    if (!error?.error) return []

    const steps = []
    
    if (error.error.code === 'CONFIG_ERROR') {
      if (config.environment === 'production') {
        steps.push(
          'Run the environment extraction script: npm run extract-api-config',
          'Verify Terragrunt deployment is complete: cd infrastructure/live/prod && terragrunt output',
          'Check that API Gateway is deployed and accessible',
          'Rebuild the frontend with the correct environment variables'
        )
      } else {
        steps.push(
          'Set VITE_API_URL in your .env file',
          'Run: npm run extract-api-config (if using deployed backend)',
          'Or set manually: VITE_API_URL=https://your-api-gateway-url.amazonaws.com',
          'Restart the development server: npm run dev'
        )
      }
    }

    return steps
  }

  return (
    <div className={`p-4 border rounded-lg ${getStatusColor()} ${className}`}>
      <div className="flex items-start space-x-3">
        {getStatusIcon()}
        <div className="flex-1">
          <h3 className="text-lg font-semibold mb-2 flex items-center">
            API Configuration Status
            {config.environment === 'development' && (
              <span className="ml-2 px-2 py-1 text-xs bg-blue-100 text-blue-800 rounded">
                DEV
              </span>
            )}
          </h3>
          
          <div className="space-y-3">
            <div className="flex items-center space-x-2">
              <span className="font-medium">Status:</span>
              <span className={`px-2 py-1 rounded text-sm font-medium ${
                isValid 
                  ? config.environment === 'development' && config.baseUrl === 'https://api.example.com'
                    ? 'bg-yellow-100 text-yellow-800'
                    : 'bg-green-100 text-green-800'
                  : 'bg-red-100 text-red-800'
              }`}>
                {isValid 
                  ? config.environment === 'development' && config.baseUrl === 'https://api.example.com'
                    ? 'Development Mode'
                    : 'Connected'
                  : 'Configuration Error'
                }
              </span>
            </div>
            
            {showDetails && (
              <>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3 text-sm">
                  <div>
                    <span className="font-medium">Environment:</span>
                    <span className="ml-2 text-gray-700">{config.environment}</span>
                  </div>
                  <div>
                    <span className="font-medium">Region:</span>
                    <span className="ml-2 text-gray-700">{config.region}</span>
                  </div>
                </div>
                
                <div className="text-sm">
                  <span className="font-medium">API Endpoint:</span>
                  <div className="mt-1 flex items-center space-x-2">
                    <code className="bg-gray-100 px-2 py-1 rounded text-xs break-all flex-1">
                      {config.baseUrl}
                    </code>
                    <button
                      onClick={() => copyToClipboard(config.baseUrl)}
                      className="p-1 hover:bg-gray-200 rounded transition-colors"
                      title="Copy URL"
                    >
                      <Copy className="h-4 w-4" />
                    </button>
                    {config.baseUrl !== 'https://api.example.com' && (
                      <a
                        href={config.baseUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="p-1 hover:bg-gray-200 rounded transition-colors"
                        title="Open in new tab"
                      >
                        <ExternalLink className="h-4 w-4" />
                      </a>
                    )}
                  </div>
                  {copied && (
                    <p className="text-xs text-green-600 mt-1">Copied to clipboard!</p>
                  )}
                </div>
              </>
            )}
            
            {error && (
              <div className="mt-4 p-3 bg-red-50 border border-red-200 rounded">
                <div className="flex items-start space-x-2">
                  <XCircle className="h-5 w-5 text-red-500 mt-0.5 flex-shrink-0" />
                  <div className="flex-1">
                    <h4 className="font-medium text-red-800 mb-2">Configuration Error</h4>
                    <p className="text-red-700 text-sm mb-3">{error.error?.message}</p>
                    
                    {getTroubleshootingSteps().length > 0 && (
                      <div className="mb-3">
                        <h5 className="font-medium text-red-800 text-sm mb-2">Troubleshooting Steps:</h5>
                        <ol className="text-sm text-red-700 space-y-1">
                          {getTroubleshootingSteps().map((step, index) => (
                            <li key={index} className="flex items-start">
                              <span className="mr-2 font-medium">{index + 1}.</span>
                              <span>{step}</span>
                            </li>
                          ))}
                        </ol>
                      </div>
                    )}
                    
                    {showDetails && error.error?.details && (
                      <details className="text-xs">
                        <summary className="cursor-pointer text-red-600 hover:text-red-800">
                          Show technical details
                        </summary>
                        <div className="mt-2 p-2 bg-red-100 rounded">
                          <div><strong>Error Code:</strong> {error.error.code}</div>
                          <div><strong>Retryable:</strong> {error.error.retryable ? 'Yes' : 'No'}</div>
                          <pre className="mt-2 text-xs overflow-auto">
                            {JSON.stringify(error.error.details, null, 2)}
                          </pre>
                        </div>
                      </details>
                    )}
                  </div>
                </div>
              </div>
            )}
            
            {isValid && config.environment === 'development' && config.baseUrl === 'https://api.example.com' && (
              <div className="mt-4 p-3 bg-yellow-50 border border-yellow-200 rounded">
                <div className="flex items-start space-x-2">
                  <Info className="h-5 w-5 text-yellow-500 mt-0.5 flex-shrink-0" />
                  <div className="flex-1">
                    <h4 className="font-medium text-yellow-800 mb-2">Development Mode</h4>
                    <p className="text-yellow-700 text-sm mb-3">
                      You're using a placeholder API URL. The application will show mock behavior until connected to a real backend.
                    </p>
                    <div className="text-sm text-yellow-700">
                      <p className="font-medium mb-1">To connect to your deployed backend:</p>
                      <ol className="space-y-1 ml-4">
                        <li>1. Run: <code className="bg-yellow-100 px-1 rounded">npm run extract-api-config</code></li>
                        <li>2. Restart the development server: <code className="bg-yellow-100 px-1 rounded">npm run dev</code></li>
                      </ol>
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  )
}

export default ConfigurationStatus